import { DocsCallout, DocsContent, DocsLayout, H2, H3 } from '@/components/docs'

/** Reusable bullet marker matching the rest of the docs. */
function Bullet() {
  return <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
}

const DIAGRAM_CLASS =
  'p-4 rounded-lg bg-card border border-border text-xs font-mono text-foreground overflow-x-auto my-4 leading-relaxed'

export default function DocsEntityResolution() {
  return (
    <DocsLayout>
      <DocsContent
        title="Entity Resolution"
        description="How XENDREL finds duplicate entities, collects them into reviewable groups under a hidden helper node, auto-merges the obvious ones, and lets you undo any merge."
        previousPage={{ title: 'Graph Explorer', href: '/docs/graph-explorer' }}
        nextPage={{ title: 'Natural Language', href: '/docs/natural-language' }}
      >
        <H2 id="overview">Overview</H2>
        <p className="text-foreground mb-4 leading-relaxed">
          Re-ingesting documents inevitably produces many nodes that refer to the same real-world
          entity — the same person mentioned across files, the same company in two contracts. Entity
          Resolution finds those and collects everything that belongs together into a{' '}
          <strong>group</strong>: the candidate duplicates are attached to a neutral{' '}
          <strong>hidden helper node</strong>, so you review one group at a time. No entity is
          crowned as the "main" one up front — you choose which entity survives only when you merge.
        </p>
        <p className="text-foreground mb-4 leading-relaxed">
          The pipeline runs after every ingestion (and on demand via <strong>Recalculate</strong>),
          in four steps:
        </p>
        <ol className="space-y-2 my-4 text-foreground list-decimal pl-6">
          <li>
            <strong>Block</strong> — cheaply pre-group entities so only plausibly-similar ones are
            ever compared.
          </li>
          <li>
            <strong>Score &amp; cluster</strong> — fuzzy-score the candidate pairs and join them
            into connected components.
          </li>
          <li>
            <strong>Auto-merge</strong> — collapse exact duplicates from the same document
            automatically (audited).
          </li>
          <li>
            <strong>Group</strong> — attach what's left to a hidden{' '}
            <code className="px-1 rounded bg-muted text-xs">__ResolutionGroup__</code> node, for
            human review.
          </li>
        </ol>

        <H2 id="blocking">The Blocking Approach</H2>
        <p className="text-foreground mb-4 leading-relaxed">
          <strong>Blocking</strong> keeps similarity search affordable. Each entity is dropped into
          buckets keyed by a cheap feature, and only entities landing in the <em>same</em> bucket
          are compared — a full pairwise sweep would be O(n²) (≈240 billion comparisons for one
          700k-node label). Two names with no shared bucket can't be the same entity, so skipping
          them costs nothing.
        </p>
        <p className="text-foreground mb-4 leading-relaxed">
          Names are first <strong>romanized</strong> (cross-script → Latin, e.g.{' '}
          <em>"Ксения" → "kseniya"</em>) and lowercased. The blocking key is <strong>hybrid</strong>
          : every whole word <em>and</em> its sliding 3-character n-grams. The whole word keeps
          common names robust; the n-grams catch spelling / transliteration drift that shares
          substrings but no whole word.
        </p>
        <pre className={DIAGRAM_CLASS}>{`name → romanize → keys = whole tokens + 3-grams

  "Provazník Petr"   →  provaznik · petr · pro rov ova vaz ... pet etr
  "pan Provazník"    →  pan · provaznik · pro rov ova vaz ...
                              └──── shared key "provaznik" → COMPARED
  "Kseniya" / "Ksenia" → share kse · sen · eni → COMPARED (typo/translit)
  "Ježková"          →  jezkova · jez ezk zko ...   ← shares nothing → SKIPPED

rule: compare only WITHIN a shared bucket
      keys held by > 200 entities carry no signal → dropped
      Person is only ever compared to Person (per-label isolation)`}</pre>
        <DocsCallout variant="info" title="Why per-label isolation matters">
          Blocking pools all labels by name, but clustering keeps each entity type separate — a{' '}
          <code className="px-1 rounded bg-muted text-xs">Person</code> is never grouped with a{' '}
          <code className="px-1 rounded bg-muted text-xs">Company</code>, even if their names
          collide. The matcher runs over Person, Company and Organization.
        </DocsCallout>
        <p className="text-foreground mb-4 leading-relaxed">
          Scoring within a bucket uses <strong>rapidfuzz token-sort ratio</strong> —
          Levenshtein-based and word-order-independent, so <em>"Jan Novak"</em> and{' '}
          <em>"Novak Jan"</em> score 100%. Pairs at or above the threshold (default{' '}
          <strong>0.80</strong>) become candidate edges.
        </p>

        <H2 id="groups">Groups &amp; the Hidden Hub</H2>
        <p className="text-foreground mb-4 leading-relaxed">
          Candidate pairs are joined into <strong>connected components</strong> (union-find): if A
          matches B and B matches C, all three are one group. Each group is persisted by attaching
          every member to a single neutral <strong>hidden hub node</strong> (
          <code className="px-1 rounded bg-muted text-xs">__ResolutionGroup__</code>) — at most one
          edge per member, so a group of 50 entities is 50 edges, never 1,200 pairwise ones.
        </p>
        <pre className={DIAGRAM_CLASS}>{`A group attaches its members to one neutral hidden hub:

        A      B      C
         \\     |     /
          ▶ ( hidden hub ) ◀     ← __ResolutionGroup__, never shown in the graph
         /     |     \\
        D      E      F

  edge: member ─[:IN_RESOLUTION_GROUP]→ hub   (one edge per member)
  the hub is NOT one of the entities — so no "main" node is chosen here,
  and the same entity can sit in several groups (by name, by address, …)`}</pre>
        <DocsCallout variant="info" title="The hub is invisible">
          The helper node carries no <code className="px-1 rounded bg-muted text-xs">case_id</code>{' '}
          and a double-underscore label, so it is excluded from the graph, search, exploration,
          investigation and export — it exists only for the resolution surface. You resolve
          duplicates on the Entity Resolution page; the graph stays clean and shows only real,
          resolved entities.
        </DocsCallout>

        <H3 id="survivor-selection">Who Survives a Merge</H3>
        <p className="text-foreground mb-4 leading-relaxed">
          Because the hub is neutral, nothing is designated the merge target until you act. When you
          merge a group, the UI <strong>suggests</strong> the richest entity as the one to keep —
          picked <strong>language-agnostically</strong>, no honorific or "nicest name" heuristics,
          because a multi-language platform can't reliably judge that in code. The order is:
        </p>
        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <Bullet />
            <span>
              <strong className="text-foreground">Most relationships</strong> (the richest, most
              connected node),
            </span>
          </li>
          <li className="flex items-start gap-2">
            <Bullet />
            <span>
              then <strong className="text-foreground">most properties</strong>,
            </span>
          </li>
          <li className="flex items-start gap-2">
            <Bullet />
            <span>
              then a stable <strong className="text-foreground">elementId</strong> tie-break.
            </span>
          </li>
        </ul>
        <DocsCallout variant="tip" title="You always have the final say">
          The suggested survivor is only a default. Pick any member as the one to keep (the crown
          icon) before merging — the rest fold into it, fully audited. There is no in-code name
          guessing; the analyst decides.
        </DocsCallout>

        <H2 id="auto-merge">Auto-Merge &amp; Its Conditions</H2>
        <p className="text-foreground mb-4 leading-relaxed">
          The same file often mentions one entity dozens of times (e.g. an account holder on every
          transaction row), so the graph fills with near-identical copies. Those collapse{' '}
          <strong>automatically</strong> — but only under strict, safe conditions. A member is
          auto-merged into its group's representative when <strong>all</strong> of these hold:
        </p>
        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <Bullet />
            <span>
              they share the same <code className="px-1 rounded bg-muted text-xs">document_id</code>{' '}
              (same source file), and
            </span>
          </li>
          <li className="flex items-start gap-2">
            <Bullet />
            <span>
              <strong>all identity properties are equal</strong> — i.e. no conflicting field.
            </span>
          </li>
        </ul>
        <DocsCallout variant="info" title="Identity vs provenance properties">
          The equality check compares only <em>identity</em> fields. Internal{' '}
          <code className="px-1 rounded bg-muted text-xs">_</code>-prefixed stamps and provenance (
          <code className="px-1 rounded bg-muted text-xs">document_id</code>,{' '}
          <code className="px-1 rounded bg-muted text-xs">chunk_index</code>,{' '}
          <code className="px-1 rounded bg-muted text-xs">ext_id</code>,{' '}
          <code className="px-1 rounded bg-muted text-xs">source_filename</code>, …) are{' '}
          <strong>ignored</strong> — they always differ between two mentions, so requiring them to
          match would mean nothing ever auto-merges.
        </DocsCallout>
        <pre className={DIAGRAM_CLASS}>{`Provazník Petr — 40 raw nodes across 2 files

  file A: 36 identical mentions  ──auto-merge (same doc + same identity)──►  1 survivor
  file B:  4 identical mentions  ──auto-merge──►  1 survivor

  cross-document survivors (file A ↔ file B) are NOT auto-merged:
     they go into a hidden review group → human review
     (corroborate with shared account number, DOB, …)

  result: 40 raw nodes  →  2 survivors + 1 review group`}</pre>
        <p className="text-foreground mb-4 leading-relaxed">
          Every other name match — <em>across</em> documents, or not byte-for-byte identical on
          identity fields — is left as a suggestion for a human. The system collapses two more kinds
          on its own: entities of one type that share a property the schema marks{' '}
          <code className="px-1 rounded bg-muted text-xs">unique</code> (an IBAN, a phone number),
          unless their names clearly disagree, and{' '}
          <code className="px-1 rounded bg-muted text-xs">Address</code> nodes geocoded to the same
          building. Every automatic collapse is recorded and can be reverted (see Undo below).
        </p>

        <H2 id="reviewing">Reviewing &amp; Merging</H2>
        <p className="text-foreground mb-4 leading-relaxed">
          On the Entity Resolution page each group shows its members and why they were grouped. Your
          actions, all <strong>audited and reversible</strong>:
        </p>
        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <Bullet />
            <span>
              <strong className="text-foreground">Choose who survives</strong> — the crown icon
              marks the entity to keep (defaults to the richest; change it freely).
            </span>
          </li>
          <li className="flex items-start gap-2">
            <Bullet />
            <span>
              <strong className="text-foreground">Merge</strong> — fold the remaining members into
              the chosen survivor in one audited pass.
            </span>
          </li>
          <li className="flex items-start gap-2">
            <Bullet />
            <span>
              <strong className="text-foreground">Not a match</strong> — drop a member from the
              group (non-destructive; the entity stays, only its membership edge is removed).
            </span>
          </li>
        </ul>
        <p className="text-foreground mb-4 leading-relaxed">
          A merge copies each member's missing, non-internal properties onto the survivor (existing
          survivor values always win), transfers every relationship to it, then{' '}
          <code className="px-1 rounded bg-muted text-xs">DETACH DELETE</code>s the member.
          Duplicate parallel edges the transfer creates are collapsed automatically. Crucially, a
          full snapshot of each deleted node — its labels, properties <em>and</em> every edge — is
          written to the <code className="px-1 rounded bg-muted text-xs">entity_merge_audit</code>{' '}
          table <strong>before</strong> the delete, so the undo data is durable even if the process
          dies mid-merge. Once a group is resolved, its hidden hub is removed.
        </p>

        <H2 id="undo">Undo: Revert a Merge (Un-Merge)</H2>
        <p className="text-foreground mb-4 leading-relaxed">
          Every merge is reversible from its audit snapshot: open the <strong>Audit log</strong>{' '}
          from the Entity Resolution page, pick the merge and choose{' '}
          <strong>Revert this merge</strong>. Reverting does <strong>not</strong> turn back time —
          it <em>recreates</em> the merged-away node, re-wires what it can, and takes back from the
          survivor what that node gave it:
        </p>
        <pre
          className={DIAGRAM_CLASS}
        >{`audit snapshot  { labels, properties, every edge (type, dir, neighbour) }
        │  revert
        ▼
  ① CREATE a NEW node with those labels + properties
       └─ gets a FRESH elementId (Neo4j can't reuse the old one)
       └─ original id saved in  _unmerged_from
  ② re-create each saved edge to its neighbour
       └─ if the neighbour still exists → reattached
       └─ if the neighbour was itself merged/deleted → skipped (counted)
  ③ the survivor it was merged into → GIVES BACK what it absorbed from this node
       └─ edges: each edge the merge moved over remembers the merged node(s)
          it came from (_merged_from). This node is taken off that list;
          an edge left with no source is deleted
       └─ properties: one the survivor inherited from this node is removed,
          unless its value was changed since or another node that is still
          merged in supplied it too
       └─ edges + properties the survivor had before the merge → never touched
       (merges from the review queue and same-document auto-merges; the two
        other automatic folds keep less track — see the note below)`}</pre>
        <p className="text-foreground mb-4 leading-relaxed">
          So the node comes back with the same data and reconnects to surviving neighbours, as a{' '}
          <strong>new node identity</strong>, and the survivor returns to what it was before the
          merge, keeping anything changed on it since. The confirmation reports how many edges were
          restored, how many could not reconnect, and what was removed from the survivor. For a
          person, the HUMINT captures the merge had moved follow the restored node, and while
          Advanced Face Detection is on the face suggestions of both persons are rebuilt.
        </p>
        <DocsCallout variant="warning" title="Close to an inverse, not a perfect one">
          A merge from the review queue, like a same-document auto-merge, records what the survivor
          takes over, so its revert takes exactly that back. What no revert can do: (1) the restored
          node has a <em>new</em> elementId (old references won't resolve to it); (2) an edge to a
          neighbour that was itself merged away or deleted since cannot be restored and is skipped;
          (3) what two merged nodes both contributed stays on the survivor until the second merge is
          reverted too; (4) a merge can be reverted once — the record then reads <em>reverted</em>.
        </DocsCallout>
        <DocsCallout variant="info" title="Automatic merges on a unique property or an address">
          These two folds (<em>Automatic — matching unique property</em> and{' '}
          <em>Automatic — geocoded to the same building</em> in the merge record) do not note which
          edges moved, and where both nodes held a property, the folded node's value replaced the
          survivor's (its name excepted). Reverting one restores the node and removes the properties
          the survivor had gained, but the survivor keeps the edges it took over — they then exist
          on both nodes — and a replaced value is not brought back. A merge recorded before this
          bookkeeping existed reverts the same way, and keeps the gained properties too.
        </DocsCallout>

        <H2 id="on-demand">Recalculate &amp; Find More Duplicates</H2>
        <p className="text-foreground mb-4 leading-relaxed">
          Two on-demand actions sit in the Entity Resolution toolbar, both running as background
          jobs so they never block the page:
        </p>
        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <Bullet />
            <span>
              <strong className="text-foreground">Recalculate</strong> rebuilds the automatic
              name-based groups from scratch (drops the existing name groups, re-blocks,
              re-clusters, re-auto-merges). Use it after changing data or to apply matcher
              improvements to an existing case — no re-ingestion needed. Your on-demand searches are
              left untouched.
            </span>
          </li>
          <li className="flex items-start gap-2">
            <Bullet />
            <span>
              <strong className="text-foreground">Find more duplicates</strong> is a targeted
              search: pick an entity type and one of its properties from the schema, set a
              tolerance, and every set of same-type entities whose value for that property clusters
              together becomes a new review group. <strong>Nothing is merged automatically</strong>{' '}
              — these are pure suggestions you confirm like any other group. It's additive: it never
              disturbs existing groups.
            </span>
          </li>
        </ul>
        <DocsCallout variant="tip" title="Picking a tolerance">
          In <strong>Find more duplicates</strong>, 0.80 is a good default for names. Drop to ~0.70
          if you expect heavy typos or transliteration; raise to 0.90+ when matching structured
          fields like emails or identifiers where you want very few false positives. (Structured
          fields such as IBAN or phone are matched exactly after normalization, regardless of the
          slider.)
        </DocsCallout>
      </DocsContent>
    </DocsLayout>
  )
}
